import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  RideStationContext,
  useRideEmptyStationsAllowed,
  useRideStation,
} from "@ryde/redux";
import {
  DragTypeEnum,
  errorAndToastSimpleResult,
  getStationComposedId,
  isCollapsedRideStationSF,
  isRideGreyed,
  isStationDragOrderable,
  isStationRouteWaypointStation,
  IStationRideProps,
  isWaypointDragOrderable,
  TDragDropSortable,
  TStationComposedId,
  useHasPermissions,
  useIsMobile,
  useIsShuttleRideWithManualCount,
} from "@ryde/shared";

import { RideStationPassengerRow } from "./ride-station-passenger.row";
import { RideStationRow } from "./ride-station-row";
import { RideStationStyled } from "./ride-stations.styled";

export const RideStation: FC<IStationRideProps> = props => {
  const { t } = useTranslation();
  const { station, rideDto, mode, isDragClone } = props;
  const { isLocked } = station || {};
  const { isFirstStation, isLastStation } = props;
  const { isMobile } = useIsMobile();
  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    mode,
    rideDto,
  );

  const { hasPermissions: isUpdateRidePermission } = useHasPermissions({
    entity: "ride",
    action: "update",
  });

  const isCollapsed = useRecoilValue(
    isCollapsedRideStationSF([rideDto.id || 0, station.id]),
  );

  const isShuttleManualCount = useIsShuttleRideWithManualCount(rideDto);

  const rideStationHookData = useRideStation(props);

  const { removeSelectedPassengersFromRide, stationPassengers = [] } =
    rideStationHookData;

  // The accordion under a station row lists its individual passengers. It is open when the row is
  // expanded, and always open on mobile (which has no collapse toggle).
  const isPassengerAccordionExpanded = !isCollapsed || isMobile;

  // for shuttle rides with manual counting the station shows only the driver's counted
  // boarding number — not who those passengers are — so the per-passenger accordion rows are
  // suppressed even while the row is expanded.
  const shouldShowStationPassengers =
    isPassengerAccordionExpanded &&
    !isShuttleManualCount &&
    !isStationRouteWaypointStation(station);

  const deletePassenger = useEvent(
    async (
      e: React.MouseEvent,
      hoverPassId?: number | undefined,
    ): Promise<void> => {
      const result = await removeSelectedPassengersFromRide(hoverPassId);

      if (!result.success) {
        errorAndToastSimpleResult(result, t);
      }
    },
  );

  const dragData: Omit<TDragDropSortable<TStationComposedId>, "sortable"> = {
    type: DragTypeEnum.STATION,
    payload: props,
    mode,
  };

  const isStationDraggable = useMemo(() => {
    if (
      isRideGreyed(rideDto) ||
      !isUpdateRidePermission ||
      isRideHasIndependentDayAdjustment
    ) {
      return false;
    }

    if (isStationRouteWaypointStation(station)) {
      return isWaypointDragOrderable({
        rideDto,
        isLocked,
        station,
        isFirstStation,
        isLastStation,
      });
    }

    return isStationDragOrderable({
      rideDto,
      isLocked,
      isFirstStation,
      isLastStation,
      station,
    });
  }, [
    isFirstStation,
    isLastStation,
    isLocked,
    isRideHasIndependentDayAdjustment,
    isUpdateRidePermission,
    rideDto,
    station,
  ]);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: getStationComposedId(station),
    data: dragData,
    disabled: !isStationDraggable,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return (
    <RideStationContext.Provider
      value={{
        ...props,
        ...rideStationHookData,
        rideDto,
        isDragging,
        listeners,
        attributes,
        isStationDraggable,
      }}
    >
      <RideStationStyled
        $isDragging={isDragging}
        $isDragClone={isDragClone}
        style={style}
        data-e2e-id={getStationComposedId(station)}
        ref={isStationDraggable ? setNodeRef : null}
      >
        <RideStationRow />
        {shouldShowStationPassengers &&
          (stationPassengers ?? []).map(pass => (
            <RideStationPassengerRow
              key={pass.id}
              passenger={pass}
              station={station}
              rideDto={rideDto}
              deletePassengerHandler={deletePassenger}
            />
          ))}
      </RideStationStyled>
    </RideStationContext.Provider>
  );
};
